import React, { useEffect, useState } from 'react';
import { Plane, Download, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicCaymanToUKGuide() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/public/cayman-to-uk-guide`);
      if (res.ok) setContent((await res.json()).content);
    })();
  }, []);

  if (!content) return <div className="min-h-screen flex items-center justify-center text-foreground/60">Loading…</div>;

  const c = content;
  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-uk-guide">
      {/* Hero */}
      <div className="bg-primary text-white relative overflow-hidden">
        <div className="absolute -top-6 -right-4 w-52 h-52 rounded-full bg-white/5" />
        <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/5" />
        <div className="container mx-auto px-6 py-16 relative">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
            <Plane className="w-4 h-4" /> {c.brand_name}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">{c.guide_title}</h1>
          <p className="text-lg text-white/90 mb-6">{c.guide_subtitle}</p>
          <a href={`${API}/brand/cayman-to-uk-guide.pdf`} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-5 py-2.5 rounded-lg hover:bg-white/90">
            <Download className="w-4 h-4" /> Download the PDF
          </a>
        </div>
      </div>

      <div className="container mx-auto px-6 py-10 max-w-4xl">
        <p className="text-foreground/80 text-base leading-relaxed">{c.guide_intro}</p>

        {/* Facts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <h3 className="text-primary font-bold text-sm mb-1">{c[`fact_${i}_title`]}</h3>
              <p className="text-xs text-foreground/70">{c[`fact_${i}_body`]}</p>
            </div>
          ))}
        </div>

        {/* Lead-time cases */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-4">{c.lead_time_title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { headline: c.lead_case_a_headline, condition: c.lead_case_a_condition, notes: c.lead_case_a_notes, accent: 'border-primary' },
            { headline: c.lead_case_b_headline, condition: c.lead_case_b_condition, notes: c.lead_case_b_notes, accent: 'border-purple-400' },
          ].map((v, i) => (
            <div key={i} className={`bg-white border-2 rounded-xl p-5 ${v.accent}`}>
              <div className={`text-2xl font-bold ${i === 0 ? 'text-primary' : 'text-purple-700'}`}>{v.headline}</div>
              <div className="text-sm font-semibold text-foreground mt-1">{v.condition}</div>
              <div className="text-xs text-foreground/60 mt-2">{v.notes}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.timeline_title}</h2>
        <p className="italic text-sm text-foreground/60 mb-4">{c.timeline_intro}</p>
        <div className="space-y-3">
          {(c.timeline_items || []).map((it, i) => (
            <div key={i} className="flex gap-3">
              <span className="bg-purple-400 text-white text-xs font-bold rounded-full px-3 py-1 h-fit whitespace-nowrap">{it.when}</span>
              <div className="min-w-0"><div className="font-bold">{it.step}</div><div className="text-sm text-foreground/70">{it.detail}</div></div>
            </div>
          ))}
        </div>

        {/* Booking */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.booking_title}</h2>
        <p className="italic text-sm text-foreground/60 mb-4">{c.booking_intro}</p>
        <div className="space-y-2">
          {(c.booking_items || []).map((it, i) => (
            <div key={i} className="grid grid-cols-[120px_1fr] gap-3 py-2 border-b border-border last:border-0">
              <div className="font-bold text-primary text-sm">{it.heading}</div>
              <div className="text-sm text-foreground/80">{it.body}</div>
            </div>
          ))}
        </div>

        {/* Broker */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.broker_title}</h2>
        <p className="text-sm text-foreground/70 mb-4">{c.broker_intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(c.broker_recs || []).map((r, i) => (
            <div key={i} className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <div className="font-bold text-primary">{r.name}</div>
              <div className="text-xs text-foreground/70 mt-1">{r.note}</div>
            </div>
          ))}
        </div>
        <p className="italic text-xs text-foreground/60 mt-3">{c.broker_note}</p>

        {/* Docs */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.docs_title}</h2>
        <div className="space-y-2">
          {(c.docs_items || []).map((d, i) => (
            <div key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /><span className="text-sm">{d}</span></div>
          ))}
        </div>

        {/* Day of travel */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.day_title}</h2>
        <div className="space-y-3">
          {(c.day_items || []).map((it, i) => (
            <div key={i} className="flex gap-3">
              <span className="bg-purple-400 text-white text-xs font-bold rounded-full px-3 py-1 h-fit whitespace-nowrap">{it.when}</span>
              <div className="min-w-0"><div className="font-bold">{it.step}</div><div className="text-sm text-foreground/70">{it.detail}</div></div>
            </div>
          ))}
        </div>

        {/* Supplies */}
        <h2 className="text-2xl font-bold text-primary mt-10 mb-3">{c.supplies_title}</h2>
        <p className="italic text-sm text-foreground/60 mb-4">{c.supplies_intro}</p>
        <div className="space-y-2">
          {(c.supplies_items || []).map((s, i) => (
            <div key={i} className="grid grid-cols-[160px_1fr] gap-3 py-2 border-b border-border last:border-0">
              <div className="font-bold text-primary text-sm">{s.heading}</div>
              <div className="text-sm text-foreground/80">{s.body}</div>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mt-10">
          <h3 className="font-bold text-primary mb-1">A note from us</h3>
          <p className="text-sm text-foreground/80">{c.closing_note}</p>
        </div>

        {/* Contact */}
        <div className="text-center mt-10 pt-8 border-t border-border">
          <div className="font-bold text-lg">{c.contact_name}</div>
          <div className="text-sm text-foreground/60 mb-3">{c.contact_role}</div>
          <div className="text-sm text-foreground/70">
            <a href={`https://${c.contact_website}`} className="text-primary hover:underline">{c.contact_website}</a>
            {' · '}
            <a href={`mailto:${c.contact_email}`} className="text-primary hover:underline">{c.contact_email}</a>
            {' · '}
            {c.contact_phone}
          </div>
        </div>
      </div>
    </div>
  );
}
